import { useDispatch, useSelector } from 'react-redux';
import { formatMoneyKr } from '../../../utils';
import { getFormActions } from '../../../utils/form-helpers';
import formConfig from '../../../payment/payment-form-config';
import ToggleButton from '../../../components/toggle-button/ToggleButton';
import { trackEvent } from '../../../support-and-tracking/analytics-actions';
import { PAYMENT_AMOUNTS_AB_TESTING } from '../../../constants/features/constants';
import ScreenReaderOnly from '../../../accessibility/ScreenReaderOnly';
import { useFeature } from '../../../components/hooks/isFeatureEnabled';
import styles from './input-amount.module.less';
import { InputTextLike, ErrorFieldMessage } from '@sb1/ffe-form-react';
import { clearProjectErrors } from '../../project-actions';

const { setFormField } = getFormActions(formConfig);

const InputAmount = ({
    projectId,
    projectProfile = {},
    onAmountChange,
    trackingCategory = 'support',
    trackingAction = 'checkout-suggestion',
    info,
    hideDefaultAmounts,
    ...props
}) => {
    const paymentForm = useSelector((state) => state.paymentForm);
    const { amount, errors } = paymentForm;
    const allErrors = {
        ...props.errors,
        ...errors,
    };

    const dispatch = useDispatch();
    const enablePaymentAmountsABTest = useFeature(PAYMENT_AMOUNTS_AB_TESTING);

    const giveAmountChangeHandler = (event, validate) => {
        let amount = event.target.value.replace(/[^0-9.]/g, '').match(/^[0-9]+/);
        if (amount) {
            amount = parseInt(amount, 10);
        }
        if (onAmountChange) {
            onAmountChange(amount);
        }
        dispatch(clearProjectErrors());
        dispatch(setFormField('amount', amount, validate));
    };

    const setAmount = (amount) => {
        trackEvent(trackingCategory, trackingAction, { amount, 'project-id': projectId });
        dispatch(setFormField('amount', amount));
        if (onAmountChange) {
            onAmountChange(amount);
        }
    };

    let lowAmount = 200;
    let mediumAmount = 400;
    let highAmount = 800;
    let highestAmount = 1200;

    if (enablePaymentAmountsABTest) {
        lowAmount = 250;
        mediumAmount = 550;
        highAmount = 1200;
        highestAmount = 2000;
    }

    const error = allErrors.global || allErrors.amount || '';

    return (
        <div>
            {!projectProfile?.hideDefaultPaymentAmounts && !hideDefaultAmounts && (
                <fieldset aria-labelledby="suggested-amounts">
                    <ScreenReaderOnly tag={'legend'} id="suggested-amounts">
                        Foreslåtte beløp
                    </ScreenReaderOnly>
                    <div className={styles.inputAmountContainer}>
                        <ToggleButton
                            selected={amount === lowAmount}
                            className={styles.predefinedAmountButton}
                            onClick={setAmount.bind(this, lowAmount)}
                        >
                            {formatMoneyKr(lowAmount)}
                        </ToggleButton>
                        <ToggleButton
                            selected={amount === mediumAmount}
                            className={styles.predefinedAmountButton}
                            onClick={setAmount.bind(this, mediumAmount)}
                        >
                            {formatMoneyKr(mediumAmount)}
                        </ToggleButton>
                        <ToggleButton
                            selected={amount === highAmount}
                            className={styles.predefinedAmountButton}
                            onClick={setAmount.bind(this, highAmount)}
                        >
                            {formatMoneyKr(highAmount)}
                        </ToggleButton>
                        <ToggleButton
                            selected={amount === highestAmount}
                            className={styles.predefinedAmountButton}
                            onClick={setAmount.bind(this, highestAmount)}
                        >
                            {formatMoneyKr(highestAmount)}
                        </ToggleButton>
                    </div>
                </fieldset>
            )}

            <div className={styles.inputAmountWrapper}>
                <ScreenReaderOnly id="inputAmountLabel">Hvor mange kroner vil du støtte med?</ScreenReaderOnly>
                <InputTextLike
                    className={styles.inputAmount}
                    aria-labelledby="inputAmountLabel"
                    onChange={giveAmountChangeHandler}
                    onBlur={(e) => giveAmountChangeHandler(e, true)}
                    value={amount || ''}
                    autoFocus
                    inputMode="number"
                    type="number"
                    pattern="[0-9]*"
                    aria-describedby="inputAmountFormError"
                    ariaLabel={'Beløpet du vil støtte med'}
                />
                <div className={styles.inputAmountPostfix}>kr</div>
            </div>
            {info && <p className={styles.info}>{info}</p>}
            {error && (
                <ErrorFieldMessage id="inputAmountFormError" className={styles.formError}>
                    {error}
                </ErrorFieldMessage>
            )}
        </div>
    );
};

export default InputAmount;
